import React, { useCallback, useState } from "react";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  MenuItem,
  Select,
  TextField, Chip,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { BarChart, LineChart } from '@mui/x-charts';
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { oauthUserMgmt, headers } from "../utils/utils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LayersIcon from "@mui/icons-material/Layers";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { alpha, useTheme } from "@mui/material/styles";
import ProjectsPerUser from "../Dashboard/ProjectsPerUser";
import ProjectsByBoardDashboard from "../Dashboard/ProjectsByBoardDashboard";

// =====================================================
// MAIN DASHBOARD (Material UI + MUI X Charts)
// =====================================================

export default function MainDashboard() {

  const navigate = useNavigate();

  const formatDateLocal = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getDefaultDates = () => {
    const today = new Date();

    const firstDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    return {
      startDate: formatDateLocal(firstDayOfMonth), // ✅ 01-12-2025
      endDate: formatDateLocal(today),              // ✅ today
    };
  };

  const GRADIENTS = [
    {
      bg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      color: "#1565c0",
    },
    {
      bg: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
      color: "#2e7d32",
    },
    {
      bg: "linear-gradient(135deg, #fff8e1, #ffecb3)",
      color: "#ef6c00",
    },
    {
      bg: "linear-gradient(135deg, #ede7f6, #d1c4e9)",
      color: "#5e35b1",
    },
    {
      bg: "linear-gradient(135deg, #ffebee, #ffcdd2)",
      color: "#c62828",
    },
    {
      bg: "linear-gradient(135deg, #e0f2f1, #b2dfdb)",
      color: "#00695c",
    },
  ];
  const defaultDates = getDefaultDates();
  const [filters, setFilters] = React.useState({
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
    board: "",
  });

  const [dashboard, setDashboard] = React.useState(null);
  const [boards, setBoards] = React.useState([]);
  const [projectsPerUser, setProjectsPerUser] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userNameMap, setUserNameMap] = useState({});

  const [projectsPerTag, setProjectsPerTag] = useState([]);
  const [tagLabelMap, setTagLabelMap] = React.useState({});

  const [loadingTags, setLoadingTags] = React.useState(false);
  const [workflowData, setWorkflowData] = useState([]);
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);


  React.useEffect(() => {
    fetchBoards();
    loadAllDashboardData();
  }, []);


  const loadDashboard = useCallback(async () => {
    const params = new URLSearchParams();

    params.append("startDate", filters.startDate);
    params.append("endDate", filters.endDate);

    if (filters.board) {
      params.append("board", filters.board);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/main?${params}`,
        { method: "GET", headers: headers() },
      );
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error("Error dashboard data.", error);
    }
  }, [filters, setDashboard]);

  const fetchBoards = useCallback(async () => {
    try {
      const response = await fetch(
        process.env.REACT_APP_SYS_CONFIG_MGMT + "/systemConfig/config?name=Board&active=true",
        { method: "GET", headers: headers() },
      );
      const data = await response.json();
      setBoards(data);
    } catch (error) {
      console.error("Error fetching boards.", error);
    }
  }, [setBoards]);

  const fetchUserName = async (userId) => {
    if (userNameMap[userId]) return userNameMap[userId];

    try {
      const response = await fetch(
        `${process.env.REACT_APP_USER_MGMT}/admin/realms/eVyoog/users/${userId}`,
        {
          method: "GET",
          headers: oauthUserMgmt(),
        },
      );

      if (!response.ok) return userId;

      const data = await response.json();
      const username = data.firstName || userId;

      setUserNameMap((prev) => ({ ...prev, [userId]: username }));
      return username;
    } catch (error) {
      console.error("User fetch failed", error);
      return userId;
    }
  };

  const loadProjectsPerUser = useCallback(async () => {
    try {
      setLoadingUsers(true);

      const params = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate,
      });

      if (filters.board) {
        params.append("board", filters.board);
      }

      const response = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/projects-per-user?${params.toString()}`,
        {
          method: "GET",
          headers: headers()
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load projects per user");
      }

      const data = await response.json();
      // 🔹 Resolve usernames
      const enriched = await Promise.all(
        data.map(async (item) => ({
          ...item,
          username: await fetchUserName(item.userId),
        }))
      );

      setProjectsPerUser(enriched);

      // setProjectsPerUser(data || []);
    } catch (error) {
      console.error("Error loading projects per user", error);
    } finally {
      setLoadingUsers(false);
    }
  }, [filters]);

  const fetchTagLabelsForBoard = async (board) => {
    if (tagLabelMap[board]) return; // already fetched

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SYS_CONFIG_MGMT}/systemConfig/config?name=TicketLabel-${board}`,
        {
          method: "GET",
          headers: headers(),
        }
      );

      if (!response.ok) throw new Error("Failed to fetch labels");

      const result = await response.json();

      // convert list → map
      const labelMap = {};
      result.forEach((t) => {
        labelMap[t.id] = t.value;
      });

      setTagLabelMap((prev) => ({
        ...prev,
        [board]: labelMap,
      }));
    } catch (error) {
      console.error(`Error fetching labels for board ${board}`, error);
    }
  };



  const loadProjectsPerTag = React.useCallback(async () => {
    try {
      setLoadingTags(true);

      const params = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate,
      });

      if (filters.board) params.append("board", filters.board);

      const response = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/projects-per-tag?${params}`,
        { headers: headers() }
      );

      const data = await response.json();
      setProjectsPerTag(data || []);

      // 🔥 fetch labels ONLY for boards returned
      [...new Set(data.map(d => d.board))].forEach(fetchTagLabelsForBoard);

    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTags(false);
    }
  }, [filters]);



  const chartDataByBoard = React.useMemo(() => {
    if (!Array.isArray(projectsPerTag)) return {};
  
    const result = {};
  
    projectsPerTag.forEach(({ board, tagId, total, completedWithinTarget,
                              completedAfterTarget, openWithinDueDate, openAfterDueDate }) => {
  
      const tagName =
        tagLabelMap[board]?.[tagId] ?? `Tag-${tagId}`;
  
      const boardName =
        boards.find(b => String(b.id) === String(board))?.value
        || `Board-${board}`;
  
      if (!result[board]) {
        result[board] = {
          boardName,
          tags: []
        };
      }
  
      result[board].tags.push({
        tagName,
        total,
        completedWithinTarget,
        completedAfterTarget,
        openWithinDueDate,
        openAfterDueDate
      });
    });
  
    return result;
  }, [projectsPerTag, tagLabelMap, boards]);
  
  
  


  const userIds = projectsPerUser.map((d) => d.username);
  const projectCounts = projectsPerUser.map(d => Number(d.projectCount) || 0);

  const loadWorkflowCount = useCallback(async () => {
    if (!filters.board) return;

    try {
      setLoadingWorkflow(true);

      const params = new URLSearchParams({
        board: filters.board,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });

      const response = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/workflow-count?${params}`,
        {
          method: "GET",
          headers: headers(),
        },
      );

      if (!response.ok) throw new Error("Failed to load workflow count");

      const data = await response.json();
      console.log("data", data)
      setWorkflowData(data);

    } catch (e) {
      console.error(e);
    } finally {
      setLoadingWorkflow(false);
    }
  }, [filters]);

  const STAT_CARDS = [
    {
      label: "Total Created",
      valueKey: "totalCreated",
      icon: <WorkOutlineIcon sx={{ fontSize: 36 }} />,
      color: "primary",
    },
    {
      label: "Completed Projects",
      valueKey: "completedTotal",
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 36 }} />,
      color: "success",
    },
    {
      label: "Open Projects",
      valueKey: "openTotal",
      icon: <HourglassEmptyIcon sx={{ fontSize: 36 }} />,
      color: "warning",
    },
  ];

  
  const loadAllDashboardData = React.useCallback(async () => {
    await Promise.all([
      loadDashboard(),
      loadProjectsPerUser(),
      loadProjectsPerTag(),
      loadWorkflowCount() // 🔥 tags + boards come from here
    ]);
  }, [loadDashboard, loadProjectsPerUser, loadProjectsPerTag, loadWorkflowCount]);

  React.useEffect(() => {
    loadAllDashboardData();
  }, [filters, loadAllDashboardData]);
  const maxCount = Math.max(...projectCounts, 0);

  const tickStep =
    maxCount > 10000 ? 5000 :
      maxCount > 5000 ? 1000 :
        maxCount > 1000 ? 500 :
          maxCount > 100 ? 10 :
            1;

  return (
    <Box
    p={4}
    sx={{
      bgcolor: "#f0f2f5", // Very light gray/blue background
      minHeight: "100vh",
      background: "radial-gradient(circle at 10% 20%, rgb(243, 247, 255) 0%, rgb(240, 242, 245) 90%)",
    }}
  > 
    {/* ================= Header & Filters ================= */}
<Box mb={4} display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" gap={3}>
  
  {/* Page Title */}
  <Box>
      <Typography variant="h4" fontWeight={800} color="#1e293b" letterSpacing="-0.5px">
         Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary">
          Overview of Boards
      </Typography>
  </Box>

  {/* Floating Filter Card */}
  <Box 
    sx={{ 
      p:1.5, // Reduced padding for a tighter look
      pl: 2, 
      bgcolor: "white", 
      borderRadius: 50, 
      boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
      display: "flex",
      alignItems: "center",
      gap: 2,
      border: "1px solid #edf2f7" // Softer border color
    }}
  >
    {/* Board Select - Cleaned up */}
    <Select
      variant="standard"
      disableUnderline
      value={filters.board}
      displayEmpty
      onChange={(e) => setFilters({ ...filters, board: e.target.value })}
      sx={{ 
        minWidth: 140, 
        fontSize: '0.9rem', 
        fontWeight: 700, 
        color: '#475569',
        '& .MuiSelect-select': { py: 1, pr: '24px !important' } 
      }}
    >
      <MenuItem value=""><em>All Boards</em></MenuItem>
      {boards.map((board) => (
        <MenuItem key={board.id} value={board.id}>{board.value}</MenuItem>
      ))}
    </Select>

    <Box sx={{ width: '1px', height: '24px', bgcolor: '#e2e8f0', mx: 1 }} />

    {/* Start Date */}
    <TextField
      type="date"
      variant="standard"
      InputProps={{ 
        disableUnderline: true, 
        sx: { fontSize: '0.9rem', fontWeight: 600, color: '#475569' } 
      }}
      value={filters.startDate}
      onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
      sx={{ 
        width: 130,
        px: 2,
        "& input": { p: 0, cursor: 'pointer' } // Removes default input padding
      }}
    />

    <Typography sx={{ color: '#cbd5e1', fontWeight: 700 }}>-</Typography>

    {/* End Date */}
    <TextField
      type="date"
      variant="standard"
      InputProps={{ 
        disableUnderline: true, 
        sx: { fontSize: '0.9rem', fontWeight: 600, color: '#475569' } 
      }}
      value={filters.endDate}
      onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
      sx={{ 
        width: 130,
        px: 2,
        "& input": { p: 0, cursor: 'pointer' } 
      }}
    />
    
    {/* Home Button - Circular & Centered */}
    <IconButton
      onClick={() => navigate("/ticket")}
      sx={{
        bgcolor: "primary.main",
        color: "white",
        width: 40,
        height: 40,
       
        "&:hover": { 
            bgcolor: "primary.dark",
            transform: "scale(1.05)"
        },
        "& .MuiSvgIcon-root": { fontSize: "1.2rem" } // Fixes font size/centering
      }}
    >
      <HomeIcon />
    </IconButton>
  </Box>
</Box>
      {/* ================= SUMMARY STAT CARDS ================= */}

  <Grid container spacing={3} mb={3}>
    {STAT_CARDS.map((item) => (
      <Grid item xs={12} md={4} key={item.label}>
        <Card
          sx={{
            height: "100%",
            borderRadius: "24px",
            border: "1px solid rgba(255,255,255,0.3)",
            boxShadow: "0 15px 35px rgba(0,0,0,0.05)",
            position: "relative",
            overflow: "hidden",
            transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
            "&:hover": { transform: "translateY(-10px)", boxShadow: "0 25px 45px rgba(0,0,0,0.1)" },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: (theme) => alpha(theme.palette[item.color].main, 0.1),
                  color: (theme) => theme.palette[item.color].main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: "3.5rem",
                  fontWeight: 900,
                  color: (theme) => theme.palette[item.color].main,
                  lineHeight: 1,
                }}
              >
                {dashboard?.[item.valueKey] ?? 0}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, color: "#2d3748", mb: 2 }}>
              {item.label}
            </Typography>

            {/* --- Progress Bars for Detail --- */}
            {item.valueKey === "completedTotal" && (
              <Box mt={2}>
                <LinearProgress
                  variant="determinate"
                  value={dashboard?.completedTotal ? (dashboard.completedWithinTarget / dashboard.completedTotal) * 100 : 0}
                  color="success"
                  sx={{ height: 10, borderRadius: 5, bgcolor: "#edf2f7" }}
                />
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#718096", display: "block" }}>ON TIME</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800,  fontSize: "1.35rem", color: "success.main" }}>{dashboard?.completedWithinTarget ?? 0}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" sx={{ color: "#718096", display: "block" }}>LATE</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800,  fontSize: "1.35rem", color: "error.main" }}>{dashboard?.completedAfterTarget ?? 0}</Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {item.valueKey === "openTotal" && (
              <Box mt={2}>
                <LinearProgress
                  variant="determinate"
                  value={dashboard?.openTotal ? (dashboard.openWithinDueDate / dashboard.openTotal) * 100 : 0}
                  color="warning"
                  sx={{ height: 10, borderRadius: 5, bgcolor: "#edf2f7" }}
                />
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#718096", display: "block" }}>WITHIN DUE</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800,  fontSize: "1.35rem",color: "warning.dark" }}>{dashboard?.openWithinDueDate ?? 0}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" sx={{ color: "#718096", display: "block" }}>OVERDUE</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800,  fontSize: "1.35rem", color: "error.main" }}>{dashboard?.openAfterDueDate ?? 0}</Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>

<ProjectsPerUser
  projectsPerUser={projectsPerUser}
  loading={loadingUsers}
/>


<ProjectsByBoardDashboard
    chartDataByBoard={chartDataByBoard}
/>



     
      {workflowData.length > 0 && (
      <Card
  sx={{
    mt: 3, 
    borderRadius: 3,
    height: 550,               // 👈 FIXED HEIGHT
    display: "flex",
    flexDirection: "column",
  }}
>
  {/* ================= HEADER ================= */}
  <Box
    sx={{
      px: 2,
      py: 1.5,
      background: "linear-gradient(90deg, #ede7f6, #ffffff)",
      borderBottom: "1px solid #e0e0e0",
    }}
  >
    <Typography variant="h6" fontWeight={700}>
      Workflow Summary
    </Typography>
  </Box>

  {/* ================= CONTENT ================= */}
  <CardContent
    sx={{
      flex: 1,
      overflowY: "auto",       // 👈 SCROLL INSIDE
    }}
  >
      <Grid container spacing={2}>
      {workflowData.map((wf, index) => {
        const palette = GRADIENTS[index % GRADIENTS.length];

        return (
          <Grid item xs={12} sm={6} md={3} key={wf.workflow}>
            <Card
              sx={{
                height: "100%",
                borderRadius: 3,
                background: palette.bg,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                {/* Icon badge */}
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    color: palette.color,
                  }}
                >
                  <LayersIcon />
                </Box>

                {/* Workflow name */}
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: "text.secondary",
                  }}
                >
                  {wf.workflow}
                </Typography>

                {/* Count */}
                <Typography
                  variant="h3"
                  fontWeight={800}
                  sx={{ color: palette.color }}
                >
                  {wf.count}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
    </CardContent>
</Card>
)}
    </Box>
  );
}
